'use strict';

define('vb/action/actionUtils',[], () => {
  /**
   * API for invoking variables actions in a code-based action chain.
   * @category Action
   */
  class ActionUtils {
    /**
     * Iterates over an array and invokes a callback function for each element.
     * The iteration can be performed either serially or in parallel.
     *
     * @static
     * @async
     * @param {Array} arr - The array to iterate over.
     * @param {Function} callback - The callback function to invoke for each element.
     *   The callback function is called with two arguments: the current element and its index.
     * @param {Object} [invocationMode={}] - An object specifying the invocation mode.
     * @param {('serial'|'parallel')} [invocationMode.mode='serial'] - The mode of invocation.
     *   'serial' invokes the callback functions one after the other, waiting for each to complete.
     *   'parallel' invokes all callback functions concurrently and waits for all to complete.
     * @returns {Promise<Array>} A promise that resolves to an array of results from the callback invocations.
     * @throws {Error} If the invocation mode is not 'serial' or 'parallel'.
     */
    static async forEach(arr, callback, invocationMode = {}) {
      // Babel doesn't like { mode = 'serial' } = {}
      // or { mode } = { mode: 'serial' }
      const { mode = 'serial' } = invocationMode; // Keep existing contract
      if (mode === 'serial') {
        const results = [];
        for (let i = 0; i < arr.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          results.push(await callback(arr[i], i));
        }
        return results;
      }

      if (mode === 'parallel') {
        const promises = arr.map((item, i) => callback(item, i));
        return Promise.all(promises);
      }

      throw new Error(`Unsupported mode ${mode}`);
    }
  }

  return ActionUtils;
});

